import { useState } from 'react';

import { Checkbox } from '../components/Checkbox.js';
import { Icon } from '../components/Icon.js';
import type { ChannelId } from '../fixtures/domain.js';
import { NewListButton, NewListRow } from './NewListRow.js';
import { useLists, type ConversationList } from './useLists.js';
import styles from './ListPicker.module.css';

/** Where a set of chats stands against one list. */
type Membership = 'none' | 'some' | 'all';

/** Which way a press moved them. */
export type Filing = 'added' | 'removed';

interface ListPickerProps {
  /** The conversations a pick files, and an unpick takes back out. */
  channelIds: ChannelId[];
  /** Open on arrival, for the dialog whose whole subject this is. */
  defaultOpen?: boolean;
  /** A press landed, and which way it went. */
  onApplied?: (list: ConversationList, direction: Filing) => void;
  /** Flow the panel into the document below the trigger (a standard dropdown)
   *  rather than overlaying upward inside a ScopeBox's fixed foot. For hosts
   *  with no scope box — the bulk send dialog — where the dialog grows to fit. */
  inline?: boolean;
}

/**
 * "Add to list" — a checkbox per list reflecting where the chats in hand stand
 * against it (none / some / all), applying on the press (filing is cheap and
 * undone by pressing again). The trigger reads back what is filed.
 */
export function ListPicker({
  channelIds,
  defaultOpen = false,
  onApplied,
  inline = false,
}: ListPickerProps) {
  const { lists, create, addTo, removeFrom } = useLists();
  const [open, setOpen] = useState(defaultOpen);
  const [naming, setNaming] = useState(false);

  const picked = new Set(channelIds);
  const membership = (list: ConversationList): Membership => {
    const hits = list.channelIds.filter((channelId) => picked.has(channelId)).length;
    if (hits === 0) return 'none';
    return hits === picked.size ? 'all' : 'some';
  };

  const rows = lists.map((list) => ({ list, state: membership(list) }));
  // Only lists holding every picked chat are named on the trigger.
  const filedNames = rows.filter((row) => row.state === 'all').map((row) => row.list.name);
  const summary =
    filedNames.length === 0
      ? 'Add to list'
      : filedNames.length === 1
        ? (filedNames[0] ?? 'Add to list')
        : `In ${filedNames.length} lists`;

  const toggle = (list: ConversationList, state: Membership) => {
    if (state === 'all') {
      removeFrom(list.id, channelIds);
      onApplied?.(list, 'removed');
    } else {
      addTo(list.id, channelIds);
      onApplied?.(list, 'added');
    }
  };

  // Overlay hosts (a ScopeBox foot) open the panel upward over the box, so the
  // chevron points up when closed; inline hosts drop it below the trigger like a
  // plain dropdown, pointing down when closed.
  const chevron = inline
    ? open
      ? 'chevronUp'
      : 'chevronDown'
    : open
      ? 'chevronDown'
      : 'chevronUp';

  const panel = open && (
    <div className={styles.panel} data-inline={inline || undefined}>
      {naming ? (
        <NewListRow
          confirm="Add"
          onCreate={(name) => {
            // create() runs as its own statement, not inside onApplied?.(…):
            // an optional call skips evaluating its arguments when the handler
            // is absent, so a silent host (no onApplied) would file nothing.
            const created = create({ name }, channelIds);
            onApplied?.(created, 'added');
            setNaming(false);
          }}
          onCancel={() => setNaming(false)}
        />
      ) : (
        <NewListButton onClick={() => setNaming(true)} />
      )}

      {lists.length === 0
        ? !naming && <p className={styles.empty}>No lists yet.</p>
        : rows.map(({ list, state }) => (
            <label key={list.id} className={styles.row}>
              <Checkbox
                checked={state === 'all'}
                indeterminate={state === 'some'}
                label={list.name}
                onChange={() => toggle(list, state)}
              />
              <span className={styles.rowName}>{list.name}</span>
              <span className={styles.rowCount}>{list.channelIds.length}</span>
            </label>
          ))}
    </div>
  );

  const trigger = (
    <button
      type="button"
      className={styles.trigger}
      aria-expanded={open}
      onClick={() => setOpen((value) => !value)}
    >
      <Icon name={filedNames.length > 0 ? 'check' : 'bookmark'} size={16} filled={false} />
      <span className={styles.triggerLabel}>{summary}</span>
      <Icon name={chevron} size={15} />
    </button>
  );

  // Inline: trigger first, panel drops below it. Overlay: panel first, absolutely
  // positioned above the trigger (see .panel in the stylesheet).
  return (
    <div className={styles.picker}>
      {inline ? (
        <>
          {trigger}
          {panel}
        </>
      ) : (
        <>
          {panel}
          {trigger}
        </>
      )}
    </div>
  );
}
